import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useSession } from '@/lib/session';
import Icon from '@/components/icon';
import { ProductGrid, type ProductSummary } from '@/components/product-card';
import { CardSkeleton, Chip, EmptyState, ErrorState, SubHeader } from '@/components/ui';

interface ProductList {
  items: ProductSummary[];
  total: number;
}

const RECENT_KEY = 'cr:recent-searches';
const RECENT_LIMIT = 8;
const MIN_QUERY = 2;

function readRecent(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export default function SearchPage() {
  const { t } = useTranslation();
  const { authorized } = useSession();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [recent, setRecent] = useState<string[]>(readRecent);

  useEffect(() => inputRef.current?.focus(), []);

  // Печатают быстрее, чем отвечает сеть, — ждём паузу вместо запроса на каждый символ
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(id);
  }, [query]);

  const enabled = debounced.length >= MIN_QUERY;

  const results = useQuery({
    queryKey: ['search', debounced],
    queryFn: async () =>
      (await api.get<ProductList>('/catalog/products', { params: { q: debounced, limit: 30 } }))
        .data,
    enabled,
  });

  const favorites = useQuery({
    queryKey: ['favorites'],
    queryFn: async () => (await api.get<Array<{ id: string }>>('/favorites')).data,
    enabled: authorized,
  });

  /** В историю пишем только запросы, что-то нашедшие: иначе она забивается опечатками. */
  useEffect(() => {
    if (!enabled || !results.data || results.data.total === 0) return;

    setRecent((prev) => {
      const next = [debounced, ...prev.filter((item) => item !== debounced)].slice(0, RECENT_LIMIT);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  }, [enabled, debounced, results.data]);

  function clearRecent() {
    localStorage.removeItem(RECENT_KEY);
    setRecent([]);
  }

  const favoriteIds = new Set((favorites.data ?? []).map((f) => f.id));

  return (
    <div>
      <SubHeader title={t('search.title')} />

      <div className="px-4 pb-8 sm:px-6">
        <div className="flex items-center gap-3 rounded-2xl bg-surface px-4 py-3 shadow-soft">
          <Icon name="search" size={20} className="shrink-0 text-muted" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('home.searchPlaceholder')}
            enterKeyHint="search"
            className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-muted"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label={t('catalog.reset')}
              className="shrink-0 text-muted active:scale-90"
            >
              <Icon name="close" size={18} />
            </button>
          )}
        </div>

        {!enabled && recent.length > 0 && (
          <section className="mt-6">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="font-display text-lg font-semibold">{t('search.recent')}</h2>
              <button
                type="button"
                onClick={clearRecent}
                className="text-sm font-medium text-bordo active:opacity-70"
              >
                {t('search.clearRecent')}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {recent.map((item) => (
                <Chip key={item} onClick={() => setQuery(item)}>
                  {item}
                </Chip>
              ))}
            </div>
          </section>
        )}

        {!enabled && recent.length === 0 && (
          <EmptyState title={t('search.hintTitle')} hint={t('search.hint')} />
        )}

        {enabled && results.isLoading && (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {[0, 1, 2, 3].map((i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        )}

        {enabled && results.isError && <ErrorState onRetry={() => void results.refetch()} />}

        {enabled && results.data && (
          <section className="mt-6">
            {results.data.total === 0 ? (
              <EmptyState title={t('catalog.nothingFound')} hint={t('search.tryAnother')} />
            ) : (
              <>
                <p className="mb-3 text-sm text-muted">
                  {t('catalog.found', { count: results.data.total })}
                </p>
                <ProductGrid products={results.data.items} favorites={favoriteIds} />
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
